/**
 * @ngdoc service
 * @name ims.shared.data.service:Zone
 * @description
 * The ims Zone object.
 *
 * @param {ngResource} zoneResource A zone ngResource object.
 */
import { ZoneResource } from '../../resources';
import { ImsObject } from '../ims_object';
import { ZoneType } from 'app/shared/collections/zones/zone_type';
import { ZoneService } from '../../zones/zone.service';
import { SelectionType } from 'app/shared/selection/SelectionType';
import * as _ from 'lodash';
import { Cesium } from 'app/cesium';
import { randomColor } from 'app/lib/random_color';
import { ZoneHistory } from 'app/shared/collections/zones/zone_history';

export const IMS_TYPE__ZONE = 'zone';

export class Zone extends ImsObject {
    historyPoints: { [index: number]: ZoneHistory };
    type: ZoneType;
    imsType: string;
    historyIntervals;
    selectionType: string;
    show: boolean;
    editingPoints: boolean;
    private _uniqueColorString: string;
    private _uniqueCesiumColor: Cesium.Color;

    constructor(resource = new ZoneResource()) {
        super(IMS_TYPE__ZONE, resource);
        this.historyPoints = {};
        this.type = null;
        this.selectionType = SelectionType.ZONE;
        this.show = true;
        this.editingPoints = false;

        // Set default historyIntervals
        ZoneService.createHistoryIntervals(this);
    }

    applyResourceChange(resource) {
        super.applyResourceChange(resource);
        // We should never have a situation where an existing resource does not have a colour.
        // But if we do then display it as white.
        this._uniqueColorString = resource.color || (resource.id ? '#ffffff' : randomColor());
        this._uniqueCesiumColor = Cesium.Color.fromCssColorString(this._uniqueColorString);
    }

    get isDeleted(): boolean {
        return this.resource.is_deleted;
    }

    set isDeleted(boolean: boolean) {
        this.resource.is_deleted = boolean;
    }

    get siteId(): number {
        return this.resource.site_id;
    }

    set siteId(siteId: number) {
        this.resource.site_id = siteId;
    }

    get shortName(): string {
        return _.truncate(this.resource.name, { length: 25 });
    }

    get name(): string {
        return this.resource.name;
    }

    set name(name: string) {
        this.resource.name = name;
    }

    // Safe getter for the type name of zone
    get typeName(): string {
        return this.type ? this.type.name : 'Unknown';
    }

    get typeCesiumColor() {
        return this.type ? this.type.cesiumColor : Cesium.Color.WHITE;
    }

    get typeCesiumColorString() {
        return this.type ? this.type.cesiumColorString : 'WHITE';
    }

    get description(): string {
        return this.resource.description;
    }

    set description(description: string) {
        this.resource.description = description;
    }

    get typeId(): number {
        return this.resource.type_id;
    }

    set typeId(typeId: number) {
        if (this.resource.type_id) {
            throw `Cannot set Zone's type as it already has a type`;
        }
        this.resource.type_id = typeId;
    }

    get initialHistoryPoint() {
        return this.resource.initial_history_point;
    }

    set initialHistoryPoint(initialHistoryPoint: ZoneHistory) {
        this.resource.initial_history_point = initialHistoryPoint;
    }

    get color(): string {
        return this.resource.color || '#ffffff';
    }

    set color(color: string) {
        this.resource.color = color;
    }

    get uniqueColorString() {
        return this._uniqueColorString;
    }

    get uniqueCesiumColor() {
        return this._uniqueCesiumColor;
    }
}
